import { initializeApp } from "firebase/app";
import {
  AuthErrorCodes,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getFirestore,
  setDoc,
  updateDoc,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyB7lnsG-aFdr5puCwBGnLcMG2vhX7yDth4",
  authDomain: "extensionfast.firebaseapp.com",
  projectId: "extensionfast",
  storageBucket: "extensionfast.firebasestorage.app",
  messagingSenderId: "697150791848",
  appId: "1:697150791848:web:ec7c8d5ce2fd7ea90ca29d",
  measurementId: "G-X8XR844JCY",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

export async function submitEmail(email) {
  if (!email) {
    console.error("Email is required");
    return;
  }

  try {
    await fetch("/api/mail", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        type: "free-guide",
        subject: "Welcome, hope this guide helps! 🥳",
      }),
    });
  } catch (error) {
    console.error("Error adding email:", error);
  }
}

const loginEmailPassword = async ({ email, password }) => {
  const loginEmail = email;
  const loginPassword = password;

  const userCredential = await signInWithEmailAndPassword(
    auth,
    loginEmail,
    loginPassword
  );
};

const showLoginError = (error) => {
  if (error.code == AuthErrorCodes.INVALID_PASSWORD) {
    return "Wrong password. Try again.";
  } else if (error.code == AuthErrorCodes.EMAIL_EXISTS) {
    return "Email already exists.";
  } else if (error.code == AuthErrorCodes.INVALID_PASSWORD) {
    return "Password must be at least 6 characters long.";
  } else if (error.code == AuthErrorCodes.WEAK_PASSWORD) {
    return "Password must be at least 6 characters long.";
  } else if (error.code == AuthErrorCodes.INVALID_LOGIN_CREDENTIALS) {
    return "Incorrect email and/or password. Try again.";
  } else {
    return `Error: ${error.message}`;
  }
};

const createAccount = async ({ email, password }) => {
  const loginEmail = email;
  const loginPassword = password;

  const userCredential = await createUserWithEmailAndPassword(
    auth,
    loginEmail,
    loginPassword
  );

  const userRef = doc(db, "users", userCredential.user.uid);
  const userData = {
    uid: userCredential.user.uid,
    email: userCredential.user.email,
    purchased: false,
  };

  await setDoc(userRef, userData);
};

const getUser = () => {
  return auth.currentUser;
};

const logout = async () => {
  await signOut(auth);
};

const signInWithGoogle = async () => {
  const provider = new GoogleAuthProvider();

  signInWithPopup(auth, provider)
    .then(async (result) => {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      const user = result.user;

      const userRef = doc(db, "users", user.uid);
      const docSnapshot = await getDoc(userRef);

      if (!docSnapshot.exists()) {
        const userData = {
          uid: user.uid,
          email: user.email,
          purchased: false,
        };

        await setDoc(userRef, userData);
      }

      const hasUsername = await hasGithubUsername();

      if (!hasUsername) window.location.href = "/credentials";
      else window.location.href = "/extension/getting-started";
    })
    .catch((error) => {
      const errorCode = error.code;
      const errorMessage = error.message;
    });
};

const createUserFromStripe = async (user) => {
  const userRef = doc(db, "customers", user.email);
  const userData = {};

  await setDoc(userRef, userData);
};

const createUserFromCode = async () => {
  return new Promise<string | null>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userEmail = auth.currentUser?.email;
      if (!userEmail) {
        resolve("");
        return;
      }

      const userRef = doc(db, "customers", userEmail);
      const userData = {};

      await setDoc(userRef, userData);
      resolve("");
    });
  });
};

const getUserEmail = async () => {
  return new Promise<string | null>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userUID = auth.currentUser?.uid;

      if (!userUID) {
        resolve("");
        return;
      }

      const userRef = doc(db, "users", userUID);
      const docSnapshot = await getDoc(userRef);

      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        resolve(userData.email);
      }

      resolve("");
    });
  });
};

const getGithubUsername = async () => {
  return new Promise<string | null>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userUID = auth.currentUser?.uid;

      if (!userUID) {
        resolve("");
        return;
      }

      const userRef = doc(db, "users", userUID);
      const docSnapshot = await getDoc(userRef);

      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        resolve(userData.githubUsername);
      }

      resolve("");
    });
  });
};

const setGithubUsername = async (githubUsername: string) => {
  return new Promise<boolean>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userUID = auth.currentUser?.uid;

      if (!userUID) {
        return;
      }

      const userRef = doc(db, "users", userUID);
      const docSnapshot = await getDoc(userRef);

      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log(data);

        const userData = {
          uid: data.uid,
          email: data.email,
          purchased: data.purchased,
          githubUsername: githubUsername,
        };

        await updateDoc(userRef, userData);
      }

      resolve(true);
    });
  });
};

const hasGithubUsername = async () => {
  return new Promise<boolean>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userUID = auth.currentUser?.uid;

      if (!userUID) {
        resolve(false);
        return;
      }

      const userRef = doc(db, "users", userUID);
      const docSnapshot = await getDoc(userRef);

      if (docSnapshot.exists()) {
        const userData = docSnapshot.data();
        const status = userData.githubUsername !== undefined;
        resolve(status);
      }

      resolve(false);
    });
  });
};

const checkIfUserPurchased = async () => {
  return new Promise<boolean>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      const userEmail = auth.currentUser?.email;

      if (!userEmail) {
        resolve(false);
        return;
      }

      const userRef = doc(db, "customers", userEmail);
      const docSnapshot = await getDoc(userRef);

      console.log(docSnapshot);

      if (docSnapshot.exists()) {
        resolve(true);
      }

      resolve(false);
    });
  });
};

const isRedemptionCodeValid = async (code) => {
  return new Promise<boolean>(async (resolve) => {
    onAuthStateChanged(auth, async (user) => {
      if (!code) {
        resolve(false);
        return;
      }

      const userRef = doc(db, "codes", code);
      const docSnapshot = await getDoc(userRef);

      console.log(docSnapshot);

      if (docSnapshot.exists()) {
        await createUserFromCode();
        resolve(true);
      }

      resolve(false);
    });
  });
};

export {
  app,
  auth,
  createAccount,
  createUserFromStripe,
  db,
  getUser,
  getUserEmail,
  loginEmailPassword,
  logout,
  showLoginError,
  signInWithGoogle,
  checkIfUserPurchased,
  setGithubUsername,
  hasGithubUsername,
  getGithubUsername,
  isRedemptionCodeValid,
};
